import { ACTIVE_LIST_COPY, LIFECYCLE_COPY } from "./copy";
import { longDate, relativeDay } from "./format";

/**
 * Pause / resume / archive rules for the web (Figma 11115:27004).
 *
 * The server owns the state (`data.state` from the Overview; a move's
 * `data.state`; a 409's `detail.state`). These helpers only map a state the
 * server sent onto what the page draws — they never infer one. Transitions:
 *
 *   ACTIVE    → PAUSED | COMPLETED | ARCHIVED
 *   PAUSED    → ACTIVE | ARCHIVED
 *   COMPLETED → ACTIVE | ARCHIVED
 *   ARCHIVED  → terminal (Archive is final — no restore)
 */

/** The hero's icon actions per state, in the order they are drawn. */
const HERO_ACTIONS = {
  ACTIVE: ["pause", "archive"],
  PAUSED: ["resume", "archive"],
  COMPLETED: ["archive"],
};

/** What the Detail draws for `state`. `lifecycle` false hides the hero actions. */
export const lifecycleView = (state, { lifecycle = true } = {}) => ({
  badge: LIFECYCLE_COPY.badge[state] || null,
  actions: lifecycle ? HERO_ACTIONS[state] || [] : [],
  banner: LIFECYCLE_COPY.banner[state] ? state : null,
});

/** The list tabs, in the order the design draws them. */
export const LIST_TABS = ["ACTIVE", "PAUSED", "COMPLETED", "ARCHIVED"];

/** `?tab=paused` → "PAUSED"; anything unknown or missing is the Active tab. */
export const tabFromParam = (param) => {
  const up = String(param || "").toUpperCase();
  return LIST_TABS.includes(up) ? up : "ACTIVE";
};

/** "PAUSED" → "paused", the `?tab=` spelling. */
export const tabParam = (tab) => String(tab).toLowerCase();

const TAB_DATE = {
  ACTIVE: "last_activity_at",
  PAUSED: "paused_at",
  COMPLETED: "completed_at",
  ARCHIVED: "archived_at",
};

/**
 * A list card's meta line: "Last conversation · 3 days ago" on Active,
 * "Paused · Aug 26, 2026" and the like elsewhere. "" when the tab's date was
 * not sent, so the card omits the line rather than print a bare label.
 */
export const cardMeta = (item, tab, now = new Date()) => {
  const iso = item?.[TAB_DATE[tab]];
  const when = tab === "ACTIVE" ? relativeDay(iso, now) : longDate(iso);
  return when ? `${ACTIVE_LIST_COPY.meta[tab]} · ${when}` : "";
};

/**
 * The in-app list route a section's `view_all_href` points at. Only an
 * in-app path is followed; anything else falls back to the list itself.
 */
export const ACTIVE_LIST_ROUTE = "/explore/active-dialogues";

export const viewAllRoute = (href) =>
  typeof href === "string" && href.startsWith("/") && !href.startsWith("//")
    ? href
    : ACTIVE_LIST_ROUTE;
